import { Search, X } from "lucide-react";
import { SITE } from "@/constants/testIds";
import { SearchResultsDropdown } from "@/components/site/SearchResultsDropdown";
import { useCompanySearchTypeahead } from "@/hooks/use-company-search";

/**
 * Search-first typeahead used by SiteNav and HomePage hero.
 * Groups results by companies, sectors, industries and shows the KIRA score
 * inline. Keyboard: ↑ ↓ to move, Enter to open, Esc to close.
 *
 * variant="nav"  compact, monochrome — for the sticky top nav.
 * variant="hero" large, dark card — used on the homepage hero.
 */
export function CompanySearch({ variant = "nav", placeholder, autoFocus = false }) {
  const {
    query, setQuery, data, open, setOpen, active, setActive, loading, inputRef, wrapRef, go, onKey,
  } = useCompanySearchTypeahead({ variant, autoFocus });

  const isHero = variant === "hero";

  return (
    <div ref={wrapRef} className={isHero ? "relative w-full max-w-2xl" : "relative w-full max-w-md"}>
      <Search
        className={`absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 ${isHero ? "text-kira-gold" : "text-kira-textMuted"}`}
        aria-hidden
      />
      <input
        ref={inputRef}
        data-testid={isHero ? SITE.heroSearchInput : SITE.navSearchInput}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onFocus={() => query.trim() && setOpen(true)}
        onKeyDown={onKey}
        placeholder={placeholder || (isHero ? "Search Reliance, TCS, WAKEFIT, BFSI…" : "Search companies, symbols…")}
        className={
          isHero
            ? "w-full rounded-xl border border-kira-border bg-kira-surface/90 pl-11 pr-24 py-4 text-base text-white placeholder:text-kira-textMuted focus:border-kira-gold focus:outline-none backdrop-blur-md shadow-[0_0_0_1px_rgba(212,175,55,0.05)]"
            : "w-full rounded-md border border-kira-border bg-kira-surface/70 pl-9 pr-14 py-2 text-sm text-white placeholder:text-kira-textMuted focus:border-kira-gold focus:outline-none"
        }
      />
      {query && (
        <button
          type="button"
          onClick={() => { setQuery(""); inputRef.current?.focus(); }}
          className={`absolute ${isHero ? "right-16" : "right-9"} top-1/2 -translate-y-1/2 text-kira-textMuted hover:text-white`}
          aria-label="Clear search"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}
      {!isHero && (
        <kbd className="hidden md:inline-flex absolute right-2 top-1/2 -translate-y-1/2 items-center gap-0.5 rounded border border-kira-border bg-black/40 px-1.5 py-0.5 font-mono text-[10px] text-kira-textMuted">
          ⌘K
        </kbd>
      )}
      {isHero && (
        <span className="hidden sm:inline-flex absolute right-4 top-1/2 -translate-y-1/2 items-center gap-1 font-mono text-[11px] text-kira-textMuted">
          {loading ? "Searching…" : "Press ↵"}
        </span>
      )}

      {open && (
        <SearchResultsDropdown
          query={query}
          data={data}
          active={active}
          setActive={setActive}
          onSelectFacet={go}
          onSelectResult={() => { setOpen(false); setQuery(""); }}
        />
      )}
    </div>
  );
}
